<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted, onBeforeMount } from 'vue';
import { GridStack, GridStackOptions, GridItemHTMLElement, GridStackNode, GridStackWidget } from 'gridstack';
import Loader from '@/components/common/Loader.vue';
import { WidgetModel } from '../../services/DashboardsService';
import WidgetContainer from './WidgetContainer.vue';
import 'gridstack/dist/gridstack.css';
import { delay } from 'lodash';

const props = withDefaults(defineProps<{
    widgets: WidgetModel[];
    design?: boolean;
}>(), {
    design: false
});
const emit = defineEmits<{
    (e: 'menu'): void;
    (e: 'edit', widget: WidgetModel): void;
    (e: 'delete', widget: WidgetModel): void;
}>();

const CELL_HEIGHT = 135;
const COLUMN_NUMBER = 12;

let grid: GridStack | null = null;

const loaded = ref<boolean>(false);
const design = computed(() => props.design);

onMounted(async () =>
{
    loaded.value = true;

    await nextTick();

    const gridConfig: GridStackOptions = {
        acceptWidgets: true,
        staticGrid: !design.value, // Disable drag
        cellHeight: CELL_HEIGHT, // Height of one column
        float: false,
        columnOpts: {
            breakpoints: [{ w: 768, c: 1 }],
            columnMax: COLUMN_NUMBER
        },
    };

    grid = GridStack.init(gridConfig);

    grid.on('change', (event: Event, items: GridItemHTMLElement | GridStackNode | GridStackNode[]) =>
        updateWidgetPosition(event, items)
    );
});

onUnmounted(() =>
{
    grid?.destroy();
});

function updateWidgetPosition(e: Event, items: GridItemHTMLElement | GridStackNode | GridStackNode[])
{
    if (Array.isArray(items))
    {
        items.forEach((item) =>
        {
            const widget = props.widgets.find((w) => w.id === `${item.id}`.slice(1));

            if (widget)
            {
                widget.x = item.x;
                widget.y = item.y;
            }
        });
    }
}

function addWidget()
{
    emit('menu');
}

function createWidget(widget: WidgetModel)
{
    widget.y = Math.max(0, ...props.widgets.filter(p => p.id != widget.id).map(p => p.y + p.height));

    grid.makeWidget(`#widget-${widget.id}`, {
        id: `widget-${widget.id}`,
        x: 0,
        y: widget.y,
        w: widget.width,
        h: widget.height,
        noResize: true
    });
}

function updateWidget(widget: WidgetModel)
{
    grid.update(`#widget-${widget.id}`, {
        id: `widget-${widget.id}`,
        h: widget.height,
        w: widget.width
    });
}

function removeWidget(widget: WidgetModel)
{
    grid.removeWidget(`#widget-${widget.id}`, false);
}

function getWidgets()
{
    const items = grid.save();

    if (Array.isArray(items))
    {
        return items.map((item: GridStackWidget) =>
        {
            const widget = props.widgets.find((w) => w.id === item.id.replace('widget-', ''));

            return {
                ...widget,
                x: item.x,
                y: item.y,
                width: item.w,
                height: item.h
            };
        });
    }

    return [];
}

const changeSettings = (widget: WidgetModel) => emit('edit', widget);
const deleteWidget = (widget: WidgetModel) => emit('delete', widget);

defineExpose({
    createWidget,
    updateWidget,
    removeWidget,
    getWidgets
});
</script>

<template>
    <Transition name="fade">
        <div v-if="loaded" class="dashboard-container position-relative d-flex flex-column flex-fill">
            <div class="grid-stack position-relative z-2 h-100">
                <template v-for="widget in props.widgets" :key="widget.id">
                    <WidgetContainer :widget="widget" :design="props.design" @edit="changeSettings" @delete="deleteWidget" />
                </template>
            </div>
            <div v-if="design && !phone && props.widgets.length" class="grid-placeholder">
                <div v-for="item in COLUMN_NUMBER" :key="item" class="column"></div>
            </div>
            <div class="dashboard-empty z-3 h-100 flex-1 d-flex align text-center flex-column justify-content-center align-items-center" v-if="!props.widgets.length">
                <i class="dashboard-icon fa-duotone fa-grid-horizontal"></i>
                <h2 class="dashboard-h2 dashboard-h2-margin-bottom">
                    {{ $t('[[[Ten dashboard nie zawiera żadnych widgetów]]]') }}
                </h2>
                <p v-if="design" class="dashboard-p">
                    {{ $t('[[[Dodaj jeden lub więcej widgetów, aby uzyskać wgląd w postępy swojego zespołu.]]]') }}
                </p>
                <ActionButton variant="success" icon="far fa-plus" class="dashboard-button" :text="$t('[[[Dodaj widget]]]')" @click.stop="addWidget" v-if="design" />
            </div>
        </div>
        <div v-else class="position-absolute top-50 start-50 translate-middle">
            <Loader />
        </div>
    </Transition>
</template>

<style lang="scss" scoped>
.grid-placeholder {
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 1;
    padding: 10px;
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(v-bind(COLUMN_NUMBER), 1fr);

    .column {
        background: var(--bs-gray-200);
        opacity: 0.5;
        border-radius: 5px;
    }
}

.dashboard {
    &-header {
        min-height: 49.5px;
    }

    &-h2 {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0;

        &-margin-bottom {
            margin-bottom: 10px;
        }
    }

    &-p {
        margin-bottom: 10px;
    }

    &-container {
        margin: -10px;
    }

    &-icon {
        font-size: 9rem;
        margin-bottom: 3px;
        color: var(--bs-gray-500);
    }

    &-empty {
        padding: 3.5rem 1rem;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 100%;
    }

    &-button {
        margin-bottom: 10rem;
    }
}
</style>
